/**
 * Audit Log Tab Component
 *
 * Displays per-account user activity audit log with search, entity-type
 * filtering, and server-side pagination. Sourced from the AuditLog table.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@respectly/ui';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  FilePlus,
  FileEdit,
  Trash2,
  Send,
  ShieldCheck,
  PenLine,
  Eye,
  Download,
  Upload,
  ScrollText,
  Search,
  User,
  Monitor,
  MapPin,
} from 'lucide-react';
import type { AccountAuditEntry } from '../../../stores/useAccountsStore';
import { useAccountsStore } from '../../../stores/useAccountsStore';

// ── Props ────────────────────────────────────────────────────────

interface AuditLogTabProps {
  accountId: string;
}

// ── Action config ────────────────────────────────────────────────

type ActionConfig = {
  label: string;
  badgeClass: string;
  borderClass: string;
  Icon: React.ElementType;
};

const ACTION_CONFIG: Record<string, ActionConfig> = {
  create: {
    label: 'Created',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
    borderClass: 'border-l-emerald-500',
    Icon: FilePlus,
  },
  update: {
    label: 'Updated',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
    borderClass: 'border-l-amber-500',
    Icon: FileEdit,
  },
  delete: {
    label: 'Deleted',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
    borderClass: 'border-l-red-500',
    Icon: Trash2,
  },
  send: {
    label: 'Sent',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
    borderClass: 'border-l-blue-500',
    Icon: Send,
  },
  sign: {
    label: 'Signed',
    badgeClass: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400',
    borderClass: 'border-l-violet-500',
    Icon: PenLine,
  },
  consent_change: {
    label: 'Consent',
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
    borderClass: 'border-l-orange-500',
    Icon: ShieldCheck,
  },
  view: {
    label: 'Viewed',
    badgeClass: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    borderClass: 'border-l-slate-400',
    Icon: Eye,
  },
  export: {
    label: 'Exported',
    badgeClass: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400',
    borderClass: 'border-l-teal-500',
    Icon: Download,
  },
  import: {
    label: 'Imported',
    badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400',
    borderClass: 'border-l-indigo-500',
    Icon: Upload,
  },
};

const DEFAULT_ACTION_CONFIG: ActionConfig = {
  label: 'Action',
  badgeClass: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  borderClass: 'border-l-slate-400',
  Icon: FileText,
};

// Entity type display labels
const ENTITY_LABELS: Record<string, string> = {
  campaign: 'Campaign',
  contact: 'Contact',
  document: 'Document',
  form: 'Form',
  support_message: 'Support Message',
  support_request: 'Support Request',
  tag: 'Tag',
  user: 'User',
  voice_script: 'Voice Script',
  template: 'Template',
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'campaign', label: 'Campaigns' },
  { value: 'contact', label: 'Contacts' },
  { value: 'document', label: 'Documents' },
  { value: 'form', label: 'Forms' },
  { value: 'support_request', label: 'Support' },
  { value: 'user', label: 'Users' },
];

// ── Avatar ───────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  { from: 'from-violet-500', to: 'to-violet-700' },
  { from: 'from-sky-500', to: 'to-sky-700' },
  { from: 'from-emerald-500', to: 'to-emerald-700' },
  { from: 'from-amber-400', to: 'to-amber-600' },
  { from: 'from-rose-500', to: 'to-rose-700' },
  { from: 'from-indigo-500', to: 'to-indigo-700' },
  { from: 'from-teal-500', to: 'to-teal-700' },
];

function getAvatarGradient(name: string) {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[idx];
}

// ── Formatting ───────────────────────────────────────────────────

function formatTimestamp(date: Date | string) {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' · ' + d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function toLabel(str: string) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Changes summary ───────────────────────────────────────────────

function ChangesSummary({ changes }: { changes: Record<string, unknown> | null }) {
  if (!changes) return null;

  // Array form: [{field, from, to}]
  if (Array.isArray(changes) && changes.length > 0) {
    const shown = (changes as Array<{ fieldLabel?: string; field?: string; from?: unknown; to?: unknown }>).slice(0, 2);
    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        {shown.map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 rounded px-1.5 py-0.5 border border-slate-100 dark:border-slate-700">
            <span className="font-medium text-slate-600 dark:text-slate-300">{c.fieldLabel ?? c.field}</span>
            {c.to !== undefined && (
              <span className="text-slate-400">→ {String(c.to)}</span>
            )}
          </span>
        ))}
        {changes.length > 2 && (
          <span className="text-xs text-slate-400 dark:text-slate-500 self-center">+{changes.length - 2} more</span>
        )}
      </div>
    );
  }

  // Object form: { key: value } — show first 2 meaningful keys
  if (typeof changes === 'object' && !Array.isArray(changes)) {
    const entries = Object.entries(changes as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .slice(0, 2);
    if (entries.length === 0) return null;
    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        {entries.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 rounded px-1.5 py-0.5 border border-slate-100 dark:border-slate-700">
            <span className="font-medium text-slate-600 dark:text-slate-300">{toLabel(k)}</span>
            <span className="text-slate-400">{String(v)}</span>
          </span>
        ))}
      </div>
    );
  }

  return null;
}

// ── Loading skeleton ─────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-4 rounded-lg border border-slate-100 dark:border-slate-800 border-l-4 border-l-slate-200 dark:border-l-slate-700 p-4 animate-pulse"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Skeleton className="h-8 w-8 rounded-full shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2 min-w-0">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-4 w-16 rounded-full" />
                <Skeleton className="h-4 w-36" />
              </div>
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
            <Skeleton className="h-7 w-7 rounded-full" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="rounded-2xl bg-slate-100 dark:bg-slate-800 p-4">
        <ScrollText className="h-10 w-10 text-slate-400" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">
        {hasFilters ? 'No matching events' : 'No audit events'}
      </h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 text-center max-w-xs">
        {hasFilters
          ? "Try adjusting your search or filter to find what you're looking for."
          : 'User activity events will appear here as the account is used.'}
      </p>
    </div>
  );
}

// ── Event row ────────────────────────────────────────────────────

function EventRow({ event }: { event: AccountAuditEntry }) {
  const config = ACTION_CONFIG[event.action] ?? DEFAULT_ACTION_CONFIG;
  const { Icon } = config;

  const entityLabel = ENTITY_LABELS[event.entityType] ?? toLabel(event.entityType);
  const actionLabel = ACTION_CONFIG[event.action]?.label ?? toLabel(event.action);

  const performerName = event.user
    ? `${event.user.firstName} ${event.user.lastName}`
    : null;
  const gradient = performerName ? getAvatarGradient(performerName) : null;

  return (
    <div
      className={[
        'group flex items-start gap-4 rounded-lg border border-slate-100 dark:border-slate-800',
        'border-l-4 p-4 transition-colors',
        config.borderClass,
        'hover:bg-slate-50/80 dark:hover:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700',
      ].join(' ')}
    >
      {/* Icon + Event info */}
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="mt-0.5 shrink-0">
          <div className={['flex h-8 w-8 items-center justify-center rounded-full', config.badgeClass].join(' ')}>
            <Icon className="h-4 w-4" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className={['inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold', config.badgeClass].join(' ')}>
              {actionLabel}
            </span>
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
              {entityLabel}
            </span>
          </div>

          {/* Entity name */}
          {event.entityName && (
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate leading-snug">
              {event.entityName}
            </p>
          )}

          {/* Changes summary */}
          <ChangesSummary changes={event.changes} />

          {/* IP / device (subtle) */}
          {event.ipAddress && event.ipAddress !== '::1' && (
            <div className="mt-1 flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
              <MapPin className="h-3 w-3" />
              <span>{event.ipAddress}</span>
              {event.userAgent && (
                <>
                  <Monitor className="h-3 w-3 ml-1" />
                  <span className="truncate max-w-[200px]">
                    {event.userAgent.includes('Chrome') ? 'Chrome' :
                     event.userAgent.includes('Firefox') ? 'Firefox' :
                     event.userAgent.includes('Safari') ? 'Safari' : 'Browser'}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Performer + Timestamp */}
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        {performerName && gradient ? (
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-xs text-slate-500 dark:text-slate-400 max-w-[120px] truncate text-right leading-tight">
              {performerName}
            </span>
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} text-white text-[10px] font-bold shadow-sm`}
              title={`${performerName} (${event.user!.email})`}
            >
              {event.user!.firstName.charAt(0).toUpperCase()}
              {event.user!.lastName.charAt(0).toUpperCase()}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
            <User className="h-3.5 w-3.5" />
            <span>System</span>
          </div>
        )}
        <p className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
          {formatTimestamp(event.createdAt)}
        </p>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────

export function AuditLogTab({ accountId }: AuditLogTabProps) {
  const { accountAudit, accountAuditPagination, isLoadingAudit, loadAccountAudit } =
    useAccountsStore();

  const [searchInput, setSearchInput] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setSearchInput('');
    setEntityTypeFilter('all');
    loadAccountAudit(accountId, { page: 1, search: '', eventType: '' });
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [accountId]);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadAccountAudit(accountId, {
        page: 1,
        search: value,
        eventType: entityTypeFilter === 'all' ? '' : entityTypeFilter,
      });
    }, 300);
  }

  function handleEntityTypeChange(value: string) {
    setEntityTypeFilter(value);
    loadAccountAudit(accountId, {
      page: 1,
      search: searchInput,
      eventType: value === 'all' ? '' : value,
    });
  }

  function handlePageChange(page: number) {
    loadAccountAudit(accountId, {
      page,
      search: searchInput,
      eventType: entityTypeFilter === 'all' ? '' : entityTypeFilter,
    });
  }

  const hasFilters = !!searchInput || entityTypeFilter !== 'all';
  const pagination = accountAuditPagination;
  const showPagination = !isLoadingAudit && pagination && pagination.totalPages > 1;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Title + count */}
          <div className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-slate-500" />
            <CardTitle className="text-base">
              Audit Log
              {pagination && (
                <span className="ml-2 text-sm font-normal text-slate-400">
                  ({pagination.total.toLocaleString()}{' '}
                  {pagination.total === 1 ? 'event' : 'events'})
                </span>
              )}
            </CardTitle>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Search events..."
                value={searchInput}
                onChange={handleSearchChange}
                className="pl-8 h-8 text-sm w-[170px] bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              />
            </div>
            <Select value={entityTypeFilter} onValueChange={handleEntityTypeChange}>
              <SelectTrigger className="h-8 text-sm w-[130px] bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {isLoadingAudit ? (
          <LoadingSkeleton />
        ) : accountAudit.length === 0 ? (
          <EmptyState hasFilters={hasFilters} />
        ) : (
          <div className="space-y-2">
            {accountAudit.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {showPagination && (
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination!.page - 1)}
              disabled={pagination!.page <= 1}
              className="h-8 gap-1"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </Button>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Showing{' '}
              {((pagination!.page - 1) * pagination!.pageSize + 1).toLocaleString()}–
              {Math.min(pagination!.page * pagination!.pageSize, pagination!.total).toLocaleString()}
              {' '}of {pagination!.total.toLocaleString()} events
            </p>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination!.page + 1)}
              disabled={pagination!.page >= pagination!.totalPages}
              className="h-8 gap-1"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
